import * as React from 'react';
import { useVisitedOffersContext } from '@cian/frontend-visited-offers-widget';
import { Responsive, useDeviceType } from '@cian/ui-kit';
import { shallowEqual, useSelector } from 'react-redux';

import { RecommendationOfferWithHover } from '../../components/RecommendationOffer';
import { RecommendationsList } from '../../components/RecommendationsList';
import { RecommendationOfferDesktopSkeleton } from '../../components/RedesignedRecommendationOfferDesktop';
import { RedesignedRecommendationsListDesktop } from '../../components/RedesignedRecommendationsListDesktop';
import { DESKTOP_REDESIGN_RECOMMENDATIONS_EXPERIMENT } from '../../constants/experiments';
import { selectRecommendations } from '../../selectors/recommendations';
import { EDeviceType } from '../../types/deviceType';
import { IApplicationState } from '../../types/redux';
import { TRecommendation } from '../../types/recommendation';
import { trackPageLoad } from '../../utils/analytics';
import { isExperimentActive } from '../../utils/experiments';
import { ContactButtonsContainer } from '../ContactButtonsContainer';
import { GeoInformationContainer } from '../GeoInformationContainer';
import { LabelsContainer } from '../LabelsContainer';
import { selectExperiments } from '../../selectors/experiments';
import { selectPageTypeSource } from '../../selectors/pageTypeSource';
import { selectUserId } from '../../selectors/user';
import { useApplicationContext } from '../../utils/applicationContext';
import { RedesignedRecommendationsListContainer } from './RedesignedRecommendationsListContainer';
import { trackEntityTeaserShow, trackRecommendationClick, trackRecommendationsView } from './tracking';

type TRecommendationClickHandler = (offer: TRecommendation, position: number) => void;
type TTrackTeaserShowHandler = (offerId: number, position: number, isMobile: boolean) => void;

type TRecommendationsListLegacyContainerProps = {
  isMobile: boolean;
  isOfferVisited(offerId: number): boolean;
  pageTypeSource: string | null;
  recommendations: TRecommendation[];
  onEntityTrackTeaserShow: TTrackTeaserShowHandler;
  onRecommendationClick: TRecommendationClickHandler;
};

const RecommendationsListLegacyContainer: React.FC<TRecommendationsListLegacyContainerProps> = ({
  isMobile,
  isOfferVisited,
  pageTypeSource,
  recommendations,
  onEntityTrackTeaserShow,
  onRecommendationClick,
}) => {
  const recommendationsList = React.useMemo(
    () =>
      recommendations.map((recOffer: TRecommendation, position: number) => (
        <div key={recOffer.offerId}>
          <RecommendationViewObserver
            callback={() => onEntityTrackTeaserShow(recOffer.offerId, position + 1, isMobile)}
          >
            <RecommendationOfferWithHover
              position={position + 1}
              isMobile={isMobile}
              geoInformation={<GeoInformationContainer category={recOffer.category} geo={recOffer.geo} />}
              offer={recOffer}
              onClick={onRecommendationClick}
              labels={<LabelsContainer visited={isOfferVisited(recOffer.offerId)} />}
              pageTypeSource={pageTypeSource}
            />
          </RecommendationViewObserver>
          <Responsive phone>
            <ContactButtonsContainer offerId={recOffer.offerId} position={position} />
          </Responsive>
        </div>
      )),
    [recommendations, isMobile, onRecommendationClick, isOfferVisited, pageTypeSource, onEntityTrackTeaserShow],
  );

  return <RecommendationsList>{recommendationsList}</RecommendationsList>;
};

const VIEW_THRESHOLD = 0.5;
const TEASER_CALLBACK_DELAY = 1000;
const REDESIGNED_DESKTOP_LOADING_SKELETON_COUNT = 8;

const RedesignedRecommendationsDesktopLoading = ({ total }: { total: number }): React.JSX.Element => (
  <RedesignedRecommendationsListDesktop total={total}>
    {Array.from({ length: REDESIGNED_DESKTOP_LOADING_SKELETON_COUNT }, (_, index) => (
      <RecommendationOfferDesktopSkeleton key={`redesigned-recommendation-desktop-loading-skeleton-${index}`} />
    ))}
  </RedesignedRecommendationsListDesktop>
);

interface IRecommendationViewObserverProps {
  callback(): void;
  children: React.ReactNode;
}

function RecommendationViewObserver({ callback, children }: IRecommendationViewObserverProps): React.JSX.Element {
  const elementRef = React.useRef<HTMLDivElement | null>(null);
  const callbackRef = React.useRef(callback);
  const hasTrackedRef = React.useRef(false);
  const timerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  React.useEffect(() => {
    const element = elementRef.current;

    if (!element || hasTrackedRef.current) {
      return;
    }

    const clearTrackTimer = () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const trackOnce = () => {
      if (hasTrackedRef.current) {
        return;
      }

      hasTrackedRef.current = true;
      callbackRef.current();
    };

    const scheduleTrack = () => {
      if (timerRef.current) {
        return;
      }

      timerRef.current = window.setTimeout(() => {
        trackOnce();
        timerRef.current = null;
      }, TEASER_CALLBACK_DELAY);
    };

    if (!('IntersectionObserver' in window)) {
      scheduleTrack();

      return clearTrackTimer;
    }

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];

        if (entry?.isIntersecting && entry.intersectionRatio >= VIEW_THRESHOLD) {
          scheduleTrack();

          return;
        }

        clearTrackTimer();
      },
      { threshold: VIEW_THRESHOLD },
    );

    observer.observe(element);

    return () => {
      clearTrackTimer();
      observer.disconnect();
    };
  }, []);

  return <div ref={elementRef}>{children}</div>;
}

export const RecommendationsContainer = (): React.JSX.Element | null => {
  const [isHydrated, setIsHydrated] = React.useState(false);
  const {
    custom: { initialDeviceType },
    telemetry,
  } = useApplicationContext();
  const recommendations = useSelector<IApplicationState, TRecommendation[]>(selectRecommendations, shallowEqual);
  const experiments = useSelector(selectExperiments);
  const pageTypeSource = useSelector<IApplicationState, string | null>(selectPageTypeSource);
  const device = useDeviceType();
  const userId = useSelector(selectUserId);
  const isMobile = device === EDeviceType.Phone;
  const isInitialDesktop = initialDeviceType === EDeviceType.Desktop;
  const isDesktop = isInitialDesktop || device === EDeviceType.Desktop;
  const isRedesignExperimentEnabled = isExperimentActive(experiments, DESKTOP_REDESIGN_RECOMMENDATIONS_EXPERIMENT, 'B');
  const shouldRenderRedesignedDesktop = isDesktop && isRedesignExperimentEnabled;
  const shouldHoldLegacyUntilClientDeviceResolved = isRedesignExperimentEnabled && !isDesktop && !isHydrated;
  const { setOfferVisited, isOfferVisited, isLoaded } = useVisitedOffersContext();
  const shownRecommendationIdsRef = React.useRef(new Set<number>());

  React.useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleRecommendationClick = React.useCallback(
    (offer: TRecommendation, position: number) => {
      if (pageTypeSource === 'chat_bot') {
        telemetry.increment('recommendation.offer.source.chat-bot');
      }
      setOfferVisited(offer.offerId);
      trackRecommendationClick({ offer, position, deviceType: device });
    },
    [pageTypeSource, setOfferVisited, device, telemetry],
  );

  React.useEffect(() => {
    if (isLoaded) {
      trackPageLoad({
        deviceType: device,
        userId,
        products: recommendations.map(({ offerId }: TRecommendation, index: number) => ({
          id: offerId,
          offerType: 'offer',
          position: index + 1,
          extra: isOfferVisited(offerId) ? { is_viewes: true } : undefined,
        })),
      });
    }
  }, [device, isLoaded, recommendations, userId, isOfferVisited]);

  React.useEffect(() => {
    if (!recommendations.length || shouldHoldLegacyUntilClientDeviceResolved) {
      return;
    }

    const newProducts = recommendations
      .map(({ offerId: id }, index) => ({
        id,
        offerType: 'offer' as const,
        position: index + 1,
      }))
      .filter(({ id }) => {
        if (shownRecommendationIdsRef.current.has(id)) {
          return false;
        }

        shownRecommendationIdsRef.current.add(id);

        return true;
      });

    if (newProducts.length) {
      trackRecommendationsView({
        deviceType: device,
        products: newProducts,
      });
    }
  }, [device, recommendations, shouldHoldLegacyUntilClientDeviceResolved]);

  const handleEntityTrackTeaserShow = React.useCallback((offerId: number, position: number, isMobile: boolean) => {
    trackEntityTeaserShow(offerId, position, isMobile);
  }, []);

  if (!recommendations.length) {
    return null;
  }

  if (shouldHoldLegacyUntilClientDeviceResolved) {
    return <RedesignedRecommendationsDesktopLoading total={recommendations.length} />;
  }

  if (shouldRenderRedesignedDesktop) {
    return (
      <RedesignedRecommendationsListContainer
        recommendations={recommendations}
        isOfferVisited={isOfferVisited}
        pageTypeSource={pageTypeSource}
        onEntityTrackTeaserShow={handleEntityTrackTeaserShow}
        onRecommendationClick={handleRecommendationClick}
      />
    );
  }

  return (
    <RecommendationsListLegacyContainer
      isMobile={isMobile}
      recommendations={recommendations}
      isOfferVisited={isOfferVisited}
      pageTypeSource={pageTypeSource}
      onEntityTrackTeaserShow={handleEntityTrackTeaserShow}
      onRecommendationClick={handleRecommendationClick}
    />
  );
};
